import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Shield, TrendingDown, Calculator, DollarSign } from 'lucide-react';

const RiskProjectionCard = ({ riskProjection = {}, currentBalance, eventSeedAmount }) => {
  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  const {
    maxSustainableStep = 0,
    totalRequiredCapital = 0,
    capitalSafetyMargin = 0,
    riskLevel = 'low',
    projectedLoss = 0
  } = riskProjection;

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'danger';
      default: return 'gray';
    }
  };

  const getRiskIcon = (level) => {
    switch (level) {
      case 'low': return Shield;
      case 'medium': return AlertTriangle;
      case 'high': return TrendingDown;
      default: return Calculator;
    }
  };

  const getRiskMessage = (level) => {
    switch (level) {
      case 'low':
        return 'Tu capital es suficiente para completar el ciclo completo con seguridad.';
      case 'medium':
        return 'Puedes continuar con precaución. Monitorea tu saldo constantemente.';
      case 'high':
        return 'Riesgo alto. Considera reducir la semilla o aumentar tu capital.';
      default:
        return 'Evaluando tu situación de riesgo...';
    }
  };

  const riskColor = getRiskColor(riskLevel);
  const RiskIcon = getRiskIcon(riskLevel);

  // Clases estáticas para evitar problemas con clases dinámicas de Tailwind
  const getRiskClasses = (level) => {
    switch (level) {
      case 'low':
        return {
          bg: 'bg-success-100 dark:bg-success-900/30',
          text: 'text-success-600 dark:text-success-400',
          border: 'border-success-200 dark:border-success-800',
          bgCard: 'bg-success-50 dark:bg-success-900/20'
        };
      case 'medium':
        return {
          bg: 'bg-warning-100 dark:bg-warning-900/30',
          text: 'text-warning-600 dark:text-warning-400',
          border: 'border-warning-200 dark:border-warning-800',
          bgCard: 'bg-warning-50 dark:bg-warning-900/20'
        };
      case 'high':
        return {
          bg: 'bg-danger-100 dark:bg-danger-900/30',
          text: 'text-danger-600 dark:text-danger-400',
          border: 'border-danger-200 dark:border-danger-800',
          bgCard: 'bg-danger-50 dark:bg-danger-900/20'
        };
      default:
        return {
          bg: 'bg-gray-100 dark:bg-gray-700',
          text: 'text-gray-600 dark:text-gray-400',
          border: 'border-gray-200 dark:border-gray-700',
          bgCard: 'bg-gray-50 dark:bg-gray-800'
        };
    }
  };

  const riskClasses = getRiskClasses(riskLevel);

  const getZoneClasses = (color) => {
    switch (color) {
      case 'success':
        return {
          bg: 'bg-success-100 dark:bg-success-900/50',
          text: 'text-success-600 dark:text-success-400',
          border: 'border-success-200 dark:border-success-800',
          bgCard: 'bg-success-50 dark:bg-success-900/20'
        };
      case 'warning':
        return {
          bg: 'bg-warning-100 dark:bg-warning-900/50',
          text: 'text-warning-600 dark:text-warning-400',
          border: 'border-warning-200 dark:border-warning-800',
          bgCard: 'bg-warning-50 dark:bg-warning-900/20'
        };
      case 'danger':
        return {
          bg: 'bg-danger-100 dark:bg-danger-900/50',
          text: 'text-danger-600 dark:text-danger-400',
          border: 'border-danger-200 dark:border-danger-800',
          bgCard: 'bg-danger-50 dark:bg-danger-900/20'
        };
      default:
        return {
          bg: 'bg-gray-100 dark:bg-gray-700',
          text: 'text-gray-400 dark:text-gray-500',
          border: 'border-gray-200 dark:border-gray-700',
          bgCard: 'bg-gray-50 dark:bg-gray-800'
        };
    }
  };

  const riskSteps = [
    {
      step: '1-5',
      label: 'Zona Segura',
      color: 'success',
      icon: Shield,
      range: `${formatCurrency(eventSeedAmount)} - ${formatCurrency(eventSeedAmount * 31)}`
    },
    {
      step: '6-9',
      label: 'Zona de Precaución',
      color: 'warning',
      icon: AlertTriangle,
      range: `${formatCurrency(eventSeedAmount * 32)} - ${formatCurrency(eventSeedAmount * 255)}`
    },
    {
      step: '10-13',
      label: 'Zona de Alto Riesgo',
      color: 'danger',
      icon: TrendingDown,
      range: `${formatCurrency(eventSeedAmount * 512)} - ${formatCurrency(eventSeedAmount * 4095)}`
    }
  ];

  return (
    <div className="card p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className={`p-3 rounded-xl ${riskClasses.bg}`}>
          <RiskIcon className={`w-6 h-6 ${riskClasses.text}`} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            ⚠️ Proyección de Riesgo
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Análisis de tu capacidad financiera
          </p>
        </div>
      </div>

      {/* Risk Status */}
      <div className={`p-4 rounded-xl border-2 mb-6 ${riskClasses.border} ${riskClasses.bgCard}`}>
        <div className="flex items-center justify-between mb-3">
          <span className={`text-sm font-medium ${riskClasses.text}`}>
            Nivel de Riesgo Actual
          </span>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${riskClasses.bg} ${riskClasses.text} uppercase`}>
            {riskLevel === 'low' ? 'Bajo' : riskLevel === 'medium' ? 'Medio' : 'Alto'}
          </span>
        </div>

        <p className={`text-sm ${riskClasses.text}`}>
          {getRiskMessage(riskLevel)}
        </p>
      </div>

      {/* Financial Metrics */}
      <div className="grid grid-cols-1 gap-4 mb-6">
        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <DollarSign className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Paso Máximo Sostenible
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Con tu capital actual
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Paso {maxSustainableStep}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              de 13 pasos
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-warning-100 dark:bg-warning-900/30 rounded-lg">
              <Calculator className="w-4 h-4 text-warning-600 dark:text-warning-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Capital Requerido Total
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Para ciclo completo
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(totalRequiredCapital)}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              13 pasos
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className={`p-2 ${riskClasses.bg} rounded-lg`}>
              <Shield className={`w-4 h-4 ${riskClasses.text}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Margen de Seguridad
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Capital disponible vs requerido
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-lg font-bold ${riskClasses.text}`}>
              {capitalSafetyMargin > 0 ? '+' : ''}{formatCurrency(capitalSafetyMargin)}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {capitalSafetyMargin > 0 ? 'Excedente' : 'Déficit'}
            </p>
          </div>
        </div>
      </div>

      {/* Risk Zones Breakdown */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
          📊 Desglose por Zonas de Riesgo
        </h4>
        
        <div className="space-y-3">
          {riskSteps.map((zone, index) => {
            const ZoneIcon = zone.icon;
            const isAccessible = maxSustainableStep >= (index === 0 ? 5 : index === 1 ? 9 : 13);
            const zoneClasses = getZoneClasses(zone.color);

            return (
              <motion.div
                key={zone.step}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center p-3 rounded-lg border-2 transition-all ${
                  isAccessible
                    ? `${zoneClasses.border} ${zoneClasses.bgCard}`
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 opacity-60'
                }`}
              >
                <div className={`p-2 rounded-lg mr-3 ${
                  isAccessible
                    ? zoneClasses.bg
                    : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                  <ZoneIcon className={`w-4 h-4 ${
                    isAccessible
                      ? zoneClasses.text
                      : 'text-gray-400 dark:text-gray-500'
                  }`} />
                </div>

                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${
                      isAccessible
                        ? 'text-gray-900 dark:text-gray-100'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      Pasos {zone.step}: {zone.label}
                    </span>
                    {isAccessible ? (
                      <span className="text-xs bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300 px-2 py-1 rounded-full">
                        Accesible
                      </span>
                    ) : (
                      <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-1 rounded-full">
                        No accesible
                      </span>
                    )}
                  </div>
                  <p className={`text-xs mt-1 ${
                    isAccessible
                      ? 'text-gray-600 dark:text-gray-400'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    Rango de apuestas: {zone.range}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Warning for high risk */}
      {riskLevel === 'high' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-6 p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg"
        >
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-danger-600 dark:text-danger-400 mt-0.5" />
            <div>
              <h5 className="text-sm font-medium text-danger-700 dark:text-danger-300 mb-1">
                ⚠️ Advertencia de Riesgo Alto
              </h5>
              <p className="text-xs text-danger-600 dark:text-danger-400">
                Tu capital actual no es suficiente para completar un ciclo completo de manera segura. 
                Considera aumentar tu capital o reducir el monto de la semilla para una mejor gestión del riesgo.
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default RiskProjectionCard;